<template>
  <div class="tools-container">
    <div class="tools-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">🧰</div>
          <h1>视频工具</h1>
        </div>
      </div>
    </div>

    <div class="tools-content">
      <div class="tools-grid">
        <button
          v-for="tool in visibleTools"
          :key="tool.key"
          class="tool-button"
          :disabled="launchingTool === tool.key || !canOpenTool(tool)"
          @click="openTool(tool)"
        >
          <span class="tool-icon">{{ tool.icon }}</span>
          <span class="tool-name">{{ tool.name }}</span>
          <span class="tool-state">{{ launchingTool === tool.key ? '启动中...' : (canOpenTool(tool) ? '点击打开' : '无权限') }}</span>
        </button>
      </div>

      <div v-if="statusMessage" class="status-message" :class="{ success: statusType === 'success', error: statusType === 'error' }">
        {{ statusMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'

export default {
  name: 'VideoTools',
  data() {
    return {
      tools: [
        { key: 'compress', name: '视频压缩', icon: '🗜️', permissionKey: 'videoTools.compress' },
        { key: 'multi_bulk', name: '多视频批量', icon: '🎬', permissionKey: 'videoTools.multi_bulk' },
        { key: 'rename', name: '视频重命名', icon: '✏️', permissionKey: 'videoTools.rename' },
        { key: 'anshuiyin', name: '视频暗水印清除', icon: '🧼', permissionKey: 'videoTools.anshuiyin' }
      ],
      launchingTool: '',
      statusType: '',
      statusMessage: '',
      buttonPermissions: {}
    }
  },
  computed: {
    visibleTools() {
      return this.tools.filter((tool) => this.canShowTool(tool))
    }
  },
  created() {
    this.fetchButtonPermissions()
  },
  methods: {
    async fetchButtonPermissions() {
      const cardNumber = localStorage.getItem('cardNumber') || ''
      if (!cardNumber) {
        this.buttonPermissions = {}
        return
      }
      try {
        const response = await axios.get(API_CONFIG.getButtonPermissionListUrl(), {
          params: { cardNumber }
        })
        if (response.data && response.data.code === 200) {
          this.buttonPermissions = response.data.data || {}
          localStorage.setItem('buttonPermissions', JSON.stringify(this.buttonPermissions))
          return
        }
      } catch (error) {
        console.error('获取按钮权限失败:', error)
      }
      const localPermissions = localStorage.getItem('buttonPermissions')
      if (localPermissions) {
        try {
          this.buttonPermissions = JSON.parse(localPermissions) || {}
        } catch (e) {
          this.buttonPermissions = {}
        }
      }
    },
    getToolPermission(tool) {
      const key = tool?.permissionKey || ''
      const value = this.buttonPermissions?.[key]
      if (!value) {
        return { visible: true, enabled: true }
      }
      return {
        visible: value.visible !== false,
        enabled: value.enabled !== false
      }
    },
    canShowTool(tool) {
      return this.getToolPermission(tool).visible
    },
    canOpenTool(tool) {
      return this.getToolPermission(tool).enabled
    },
    goBack() {
      this.$router.push('/home')
    },
    async openTool(tool) {
      if (!this.canOpenTool(tool)) {
        this.statusType = 'error'
        this.statusMessage = `${tool.name} 暂无权限`
        return
      }
      this.launchingTool = tool.key
      this.statusType = ''
      this.statusMessage = ''

      try {
        const response = await axios.post('/api/tools/open', { tool: tool.key })
        if (response.data && response.data.success) {
          this.statusType = 'success'
          this.statusMessage = `${tool.name} 已启动`
        } else {
          this.statusType = 'error'
          this.statusMessage = response.data?.message || `${tool.name} 启动失败`
        }
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.response?.data?.message || `${tool.name} 启动失败`
      } finally {
        this.launchingTool = ''
      }
    }
  }
}
</script>

<style scoped>
.tools-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
}

.tools-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-5px);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.tools-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
}

.tool-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 26px 16px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.tool-button:hover:not(:disabled) {
  transform: translateY(-4px);
  border-color: rgba(102, 126, 234, 0.8);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.tool-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.tool-icon {
  font-size: 34px;
}

.tool-name {
  font-size: 17px;
  font-weight: 600;
}

.tool-state {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.status-message {
  margin-top: 18px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
}

.status-message.success {
  background: rgba(46, 213, 115, 0.2);
  border: 1px solid rgba(46, 213, 115, 0.4);
  color: #7dffb8;
}

.status-message.error {
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff9f9f;
}
</style>
